import { FC } from 'react'
import cn from 'classnames'
import { Link, LinkButton } from 'components/extend/Link'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { formatDateToMoscow } from 'helpers/dates'
import { deliveryRulesUrl } from 'modules/order/helpers'
import { CourierDeliveryInfo, OrderItem, OrderItemPartner } from 'schema'

import { OrderHelpInfoBlockProps } from './types'

import s from './styles.module.less'

const SKELETON_SIZES: { [key in OrderItem.Status]?: number } = {
  [OrderItem.Status.NEW]: 256,
  [OrderItem.Status.DEPARTURE_COURIER_ASSIGNED]: 343,
  [OrderItem.Status.DEPARTURE_COURIER]: 343,
  [OrderItem.Status.CANCEL]: 82
}
const DEFAULT_SKELETON_SIZE = 200
function getSkeletonSize(status: OrderHelpInfoBlockProps['status']) {
  if (!status || !SKELETON_SIZES[status]) {
    return DEFAULT_SKELETON_SIZE
  }

  return SKELETON_SIZES[status]
}
export const OrderHelpInfoBlock: FC<OrderHelpInfoBlockProps> = (props) => {
  const {
    isCourier,
    status,
    cancelInfo,
    departureDate,
    departurePointTerminal,
    departurePartner,
    handleOpenModalIdentificationDocuments,
    onOpenModalTerminal,
    isLoading
  } = props

  return (
    <>
      {isLoading ? (
        <PlaceholderCell height={getSkeletonSize(status)} />
      ) : (
        <div
          className={cn(s.orderHelpInfoBlock, { [s.orderHelpInfoBlock__cancel]: status === OrderItem.Status.CANCEL })}
          data-test-id="order--help--info--block"
        >
          <h2 className={cn(s.title, { [s.title__cancel]: status === OrderItem.Status.CANCEL })}>
            {status === OrderItem.Status.CANCEL ? 'Заявка отменена' : 'Заявка принята!'}
            {status === OrderItem.Status.CANCEL ? <span>{formatDateToMoscow(cancelInfo?.date, true)}</span> : ''}
          </h2>
          {status === OrderItem.Status.CANCEL ? (
            <div className={s.cancelBlock}>
              <div className={cn(s.infoTexts, s.infoTexts__cancel)}>
                <div className={cn(s.infoText, s.infoText__cancel)}>
                  <div className={s.textSecondary}>Комментарий</div>
                  <div className={s.textPrimary}>{cancelInfo?.reason}</div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {isCourier ? (
                <div className={s.isCourierBlock}>
                  <div className={cn(s.infoTexts, { [s.infoTexts__courier]: isCourier })}>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>
                        В течение дня наш оператор свяжется с вами для уточнения адреса, параметров груза и согласования
                        времени забора груза
                      </div>
                    </div>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>
                        В назначенное время приедет курьер.
                        <br />
                        При себе необходимо иметь следующие документы:
                      </div>
                      {departurePartner?.type === OrderItemPartner.type.LEGAL ? (
                        <>
                          <div className={s.textSecondary}>
                            <a
                              className={cn(s.docLink, s.linkTo)}
                              href={deliveryRulesUrl(CourierDeliveryInfo.pointType.DEPARTURE)}
                            >
                              Документы на груз,
                            </a>
                          </div>
                          <div className={s.textSecondary}>
                            <Link
                              className={s.docLink}
                              target="_parent"
                              to="/doc/Доверенность_на_отправку_груза_ЮЛ_бланк_АО_Корса_v1.docx"
                            >
                              Доверенность на отправку груза,
                            </Link>
                          </div>
                          <div className={s.textSecondary}>
                            <LinkButton className={s.docLink} onClick={handleOpenModalIdentificationDocuments}>
                              Документ удостоверяющий личность
                            </LinkButton>
                            , указанный в доверенности
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={s.textSecondary}>
                            Паспорт или документ из{' '}
                            <LinkButton className={s.docLink} onClick={handleOpenModalIdentificationDocuments}>
                              перечня,
                            </LinkButton>
                          </div>
                          <div className={s.textSecondary}>
                            <Link
                              className={s.docLink}
                              target="_parent"
                              to="/doc/Опись_на_отправку_груза_АО_Корса_v1.docx"
                            >
                              Опись груза
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>
                        Курьер проверит документы, заберёт груз и отдаст вам экспедиторскую расписку
                      </div>
                    </div>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>
                        После замера груза на терминале сообщим итоговую стоимость перевозки
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={s.terminalBlock}>
                  <div className={cn(s.infoTexts, { [s.infoTexts__terminal]: !isCourier })}>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>
                        С {formatDateToMoscow(departureDate)} ждём ваш груз по адресу ({departurePointTerminal?.name}):
                      </div>
                      <div className={s.textSecondary}>{departurePointTerminal?.address}</div>
                      <LinkButton
                        className={s.docLink}
                        onClick={() => onOpenModalTerminal && onOpenModalTerminal('departure')}
                      >
                        Карта и режим работы
                      </LinkButton>
                    </div>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>На терминале предоставьте документы:</div>
                      {departurePartner?.type === OrderItemPartner.type.LEGAL ? (
                        <>
                          <div className={s.textSecondary}>
                            <div className={s.textSecondary}>
                              <a
                                className={cn(s.docLink, s.linkTo)}
                                href={deliveryRulesUrl(CourierDeliveryInfo.pointType.DEPARTURE)}
                              >
                                Документы на груз,
                              </a>
                            </div>
                          </div>
                          <div className={s.textSecondary}>
                            <Link
                              className={s.docLink}
                              target="_parent"
                              to="/doc/Доверенность_на_отправку_груза_ЮЛ_бланк_АО_Корса_v1.docx"
                            >
                              Доверенность на отправку груза,
                            </Link>
                          </div>
                          <div className={s.textSecondary}>
                            <LinkButton className={s.docLink} onClick={handleOpenModalIdentificationDocuments}>
                              Документ удостоверяющий личность
                            </LinkButton>
                            , указанный в доверенности
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={s.textSecondary}>
                            Паспорт или документ из{' '}
                            <LinkButton className={s.docLink} onClick={handleOpenModalIdentificationDocuments}>
                              перечня,
                            </LinkButton>
                          </div>
                          <Link
                            className={s.docLink}
                            target="_parent"
                            to="/doc/Опись_на_отправку_груза_АО_Корса_v1.docx"
                          >
                            Опись груза
                          </Link>
                        </>
                      )}
                    </div>
                    <div className={s.infoText}>
                      <div className={s.textPrimary}>После замера мы сообщим итоговую стоимость перевозки</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}
